'use client';

import { decode } from 'html-entities';
import { useState, useEffect, useRef } from 'react';

const animationTypes = [
    {
        name: 'fadeInUp',
        className: 'opacity-0 translate-y-5',
        visibleClassName: 'opacity-100 translate-y-0'
    },
    {
        name: 'fadeInDown',
        className: 'opacity-0 -translate-y-5',
        visibleClassName: 'opacity-100 translate-y-0'
    },
    {
        name: 'fadeInLeft',
        className: 'opacity-0 -translate-x-5',
        visibleClassName: 'opacity-100 translate-x-0'
    },
    {
        name: 'fadeInRight',
        className: 'opacity-0 translate-x-5',
        visibleClassName: 'opacity-100 translate-x-0'
    },
    {
        name: 'scaleIn',
        className: 'opacity-0 scale-95',
        visibleClassName: 'opacity-100 scale-100'
    }
];

function extractTextFromContent(content) {
    if (!content || !Array.isArray(content)) return '';
    return content
        .map((item) => {
            if (item.type === 'text') {
                return item.text || '';
            }
            if (item.type === 'hardBreak') {
                return ' ';
            }
            if (item.content && Array.isArray(item.content)) {
                return extractTextFromContent(item.content);
            }
            return '';
        })
        .join('');
}

function processHeadingText(headingComponent, textInit = '') {
    if (!headingComponent || !Array.isArray(headingComponent)) {
        return textInit && textInit.trim() !== '' ? [textInit] : [];
    }

    const processedTexts = headingComponent
        .map((item) => {
            if (!item) {
                return '';
            }

            if (typeof item.heading?.json?.content === 'string') {
                try {
                    const parsed = JSON.parse(item.heading_raw);
                    if (parsed && parsed.content) {
                        return extractTextFromContent(parsed.content);
                    }
                    return item.heading_raw || '';
                } catch (e) {
                    return item.heading_raw || '';
                }
            }
            if (item?.heading?.json && item?.heading?.json?.content) {
                return extractTextFromContent(item.heading.json.content);
            }
            return '';
        })
        .filter((text) => text.trim() !== '');

    if (textInit && textInit.trim() !== '') {
        return [textInit, ...processedTexts];
    }

    return processedTexts;
}

function AnimatedComponent({ headingComponent, textInit }) {
    const [currentTextIndex, setCurrentTextIndex] = useState(0);
    const [currentAnimation, setCurrentAnimation] = useState(0);
    const [isVisible, setIsVisible] = useState(true);
    const [isMobileDevice, setIsMobileDevice] = useState(false);
    const [mounted, setMounted] = useState(false);
    const timeoutRef = useRef(null);
    const resizeTimeoutRef = useRef(null);
    const intervalRef = useRef(null);

    const changeText = processHeadingText(headingComponent, textInit) || [];
    const currentText = changeText[currentTextIndex] || changeText[0] || '';

    useEffect(() => {
        setMounted(true);
        if (typeof window !== 'undefined') {
            const mediaQuery = window.matchMedia('(max-width: 768px)');
            const checkMobile = () => {
                const userAgent =
                    /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
                        window.navigator.userAgent
                    );
                return userAgent || mediaQuery.matches;
            };

            setIsMobileDevice(checkMobile());

            const handleResize = () => {
                if (resizeTimeoutRef.current) {
                    clearTimeout(resizeTimeoutRef.current);
                }

                resizeTimeoutRef.current = setTimeout(() => {
                    setIsMobileDevice(checkMobile());
                }, 150);
            };

            mediaQuery.addEventListener('change', handleResize);
            window.addEventListener('resize', handleResize, { passive: true });

            return () => {
                mediaQuery.removeEventListener('change', handleResize);
                window.removeEventListener('resize', handleResize);
                if (resizeTimeoutRef.current) {
                    clearTimeout(resizeTimeoutRef.current);
                }
            };
        }
    }, []);

    useEffect(() => {
        if (isMobileDevice || changeText.length <= 1) return;

        intervalRef.current = setInterval(() => {
            setIsVisible(false);

            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            timeoutRef.current = setTimeout(() => {
                setCurrentTextIndex((prevIndex) => (prevIndex + 1) % changeText.length);
                setCurrentAnimation((prevAnimation) => (prevAnimation + 1) % animationTypes.length);
                setIsVisible(true);
            }, 300);
        }, 3000);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [changeText.length, isMobileDevice]);

    const currentAnimationType = animationTypes[currentAnimation];
    const shouldAnimate = mounted && !isMobileDevice && changeText.length > 1;

    if (!shouldAnimate) {
        return <div className="translate-y-0 opacity-100">{currentText}</div>;
    }

    return (
        <div
            className={`transition-all duration-300 ${isVisible ? currentAnimationType.visibleClassName : currentAnimationType.className}`}>
            {currentText}
        </div>
    );
}

export function renderersHeading(headingComponent) {
    return {
        // Note: Now we receive the entire node object, not just the content.
        paragraph: ({ node }) => {
            const content = node?.content;
            if (!content || content.length === 0) return null;

            const renderContent = (item, itemIndex) => {
                if (item.marks && item.marks.some((mark) => mark.type === 'superscript')) {
                    return (
                        <AnimatedComponent
                            headingComponent={headingComponent}
                            textInit={item.text}
                            key={`sup-${itemIndex}`}>
                            {item.text}
                        </AnimatedComponent>
                    );
                }
                if (item.marks && item.marks.some((mark) => mark.type === 'bold')) {
                    return <strong key={`bold-${itemIndex}`}>{decode(item.text)} </strong>;
                }
                return <span key={`text-${itemIndex}`}>{decode(item.text)} </span>;
            };

            return (
                <h2 className="whitespace-normal text-5xl">
                    {content.map((item, index) => renderContent(item, index))}
                </h2>
            );
        }
    };
}

export function renderersSubheading(headingComponent) {
    return {
        paragraph: ({ node: { content } }) => {
            if (!content || content.length === 0) return null;

            const renderContent = (item, itemIndex) => {
                if (item.marks && item.marks.some((mark) => mark.type === 'superscript')) {
                    return (
                        <AnimatedComponent
                            headingComponent={headingComponent}
                            textInit={item.text}
                            key={`sup-${itemIndex}`}>
                            {item.text}
                        </AnimatedComponent>
                    );
                }
                if (item.marks && item.marks.some((mark) => mark.type === 'bold')) {
                    return <strong key={`bold-${itemIndex}`}>{decode(item.text)}</strong>;
                }
                return <span key={`text-${itemIndex}`}>{decode(item.text)}</span>;
            };

            return (
                <p className="mx-auto max-w-[73ch]">
                    {content.map((item, index) => renderContent(item, index))}
                </p>
            );
        }
    };
}
